import React, { useState } from "react"

import { Add } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  ListSubheader,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'

import { handleApiError, ordersService, type CreateOrderRequest, type Order } from "../../api"

const PACKAGE_OPTIONS = [
  { group: 'Lab Plans — Quest', items: [
    { id: 'Basic_Plan_Quest', label: 'Basic Plan (Quest)' },
    { id: 'Complete_Plan_Quest', label: 'Complete Plan (Quest)' },
    { id: 'Total_Clarity_Plan_Quest', label: 'Total Clarity Plan (Quest)' },
  ]},
  { group: 'Lab Plans — BioReference', items: [
    { id: 'Basic_Plan_BioReference', label: 'Basic Plan (BioReference)' },
    { id: 'Complete_Plan_BioReference', label: 'Complete Plan (BioReference)' },
    { id: 'Total_Clarity_Plan_BioReference', label: 'Total Clarity Plan (BioReference)' },
  ]},
  { group: 'Gift Products', items: [
    { id: 'Metabolic_CBC_Quest_Gift', label: 'Metabolic & CBC Panel (Quest) — Gift' },
    { id: 'Metabolic_CBC_BioReference_Gift', label: 'Metabolic & CBC Panel (BioReference) — Gift' },
  ]},
  { group: 'Content Plans', items: [
    { id: '1_week_plan', label: '1 Week Content Plan' },
    { id: '4_week_plan', label: '4 Weeks Content Plan' },
    { id: '12_week_plan', label: '12 Weeks Content Plan' },
  ]},
  { group: 'Add-ons', items: [
    { id: 'wellbeing_report', label: 'Wellness Report' },
    { id: 'Add_on_BB_MP', label: 'Add-on BB + MP' },
    { id: 'Add_on_BB', label: 'Add-on BB' },
    { id: 'Add_on_MP', label: 'Add-on MP' },
    { id: 'Add_on_5_in_one', label: 'Add-on 5-in-one' },
    { id: 'CMP_Addon_Quest', label: 'CMP Add-on (Quest)' },
    { id: 'CMP_Addon_BioReference', label: 'CMP Add-on (BioReference)' },
  ]},
  { group: '12-Week Lab Test', items: [
    { id: '12_week_lab_test', label: '12-Week Lab Test (BioReference)' },
  ]},
]

type CreateOrderDialogProps = {
  onOrderCreated: (order: Order) => void
}

export function CreateOrderDialog({ onOrderCreated }: CreateOrderDialogProps) {
  const [open, setOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState("")
  const [formData, setFormData] = useState<CreateOrderRequest>({
    email: "",
    packageId: "",
    price: 0,
    currency: "USD",
  })

  const handleInputChange = (field: keyof CreateOrderRequest) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = field === 'price' ? (Number(e.target.value) || 0) : e.target.value
    setFormData(prev => ({ ...prev, [field]: value }))
    if (error) setError("")
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.email || !formData.packageId) {
      setError("Email and package are required")
      return
    }

    try {
      setIsSubmitting(true)
      setError("")
      const priceInCents = Math.round(formData.price * 100)
      const order = await ordersService.createOrder({ ...formData, price: priceInCents })
      onOrderCreated(order)
      setFormData({ email: "", packageId: "", price: 0, currency: "USD" })
      setOpen(false)
    } catch (err) {
      setError(handleApiError(err))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setOpen(false)
    setError("")
    setFormData({ email: "", packageId: "", price: 0, currency: "USD" })
  }

  const selectedLabel = PACKAGE_OPTIONS
    .flatMap(g => g.items)
    .find(i => i.id === formData.packageId)?.label

  return (
    <>
      <Button
        variant="contained"
        startIcon={<Add />}
        onClick={() => setOpen(true)}
      >
        Create Order
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Order</DialogTitle>

        <Box component="form" onSubmit={handleSubmit}>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Manually create an order for an existing user. The user must already have an account.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="User Email"
                  type="email"
                  placeholder="user@example.com"
                  value={formData.email}
                  onChange={handleInputChange("email")}
                  disabled={isSubmitting}
                  required
                />
              </Grid>

              <Grid size={12}>
                <TextField
                  fullWidth
                  select
                  label="Package"
                  value={formData.packageId}
                  onChange={handleInputChange("packageId")}
                  disabled={isSubmitting}
                  required
                >
                  {PACKAGE_OPTIONS.flatMap(group => [
                    <ListSubheader key={group.group}>{group.group}</ListSubheader>,
                    ...group.items.map(item => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.label}
                      </MenuItem>
                    )),
                  ])}
                </TextField>
              </Grid>

              <Grid size={6}>
                <TextField
                  fullWidth
                  label="Price ($)"
                  type="number"
                  value={formData.price}
                  onChange={handleInputChange("price")}
                  disabled={isSubmitting}
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText="Enter amount in dollars (e.g. 49.99)"
                />
              </Grid>

              <Grid size={6}>
                <TextField
                  fullWidth
                  label="Currency"
                  value={formData.currency}
                  onChange={handleInputChange("currency")}
                  disabled={isSubmitting}
                />
              </Grid>
            </Grid>

            {formData.email && formData.packageId && (
              <Alert severity="info" sx={{ mt: 3 }}>
                This will create a <strong>{selectedLabel}</strong> order for{' '}
                <strong>{formData.email}</strong> at{' '}
                <strong>${formData.price.toFixed(2)} {formData.currency}</strong>.
              </Alert>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={handleClose} disabled={isSubmitting} variant="outlined">
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting} variant="contained">
              {isSubmitting ? "Creating..." : "Create Order"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  )
}
